import React, { useState, useEffect } from 'react';
import { Plus, TrendingUp, AlertCircle, Calendar, Download, Loader, Edit2, Trash2, Radio } from 'lucide-react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { useCountry } from '../../CountryContext';

import NetWorthTrendChart from './NetWorthTrendChart';
import GrowthAttributionChart from './GrowthAttributionChart';
import MonthOverMonthCard from './MonthOverMonthCard';
import AccountPerformanceTable from './AccountPerformanceTable';
import SnapshotTimeline from './SnapshotTimeline';
import CreateSnapshotModal from './CreateSnapshotModal';
import InfoTooltip from './InfoTooltip';
import OnboardingBanner from './OnboardingBanner';
import AIInsights from './AIInsights';
import GoalOverlay from './GoalOverlay';

import { getAllSnapshots, deleteSnapshot } from '../../utils/wealthTracking/snapshotManager';
import { getRecentMonths, formatMonthLabel } from '../../utils/wealthTracking/chartDataTransformers';
import { analyzeWealthSnapshots } from '../../utils/wealthTracking/insightsAnalyzer';
import { calculateNetWorth } from '../../utils/netWorthCalculations';
import { formatCurrency, getCurrencyCode } from '../../countryConfig';
import { usePortfolioPrices } from '../../hooks/usePricing';

const WealthTrackingDashboard = ({
  isDark,
  investments = [],
  accounts = [],
  cryptoHoldings = [],
  cashReserves = 0,
  exchangeRates = null // ⭐ NEW: Exchange rates for currency conversion
}) => {
  const { currentUser } = useAuth();
  const { selectedCountry } = useCountry();

  const [snapshots, setSnapshots] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSnapshot, setEditingSnapshot] = useState(null); // NEW: Track snapshot being edited
  const [deletingSnapshot, setDeletingSnapshot] = useState(null); // Track snapshot being deleted
  const [viewPeriod, setViewPeriod] = useState('12'); // 12, 24, or 'all'
  const [aiAnalysis, setAiAnalysis] = useState(null); // AI insights

  // Load snapshots, liabilities, and goals
  useEffect(() => {
    if (currentUser) {
      loadSnapshots();
      loadLiabilities();
      loadGoals();
    }
  }, [currentUser]);

  const loadSnapshots = async () => {
    setLoading(true);
    setError(null);

    try {
      const loadedSnapshots = await getAllSnapshots(currentUser.uid);
      setSnapshots(loadedSnapshots);

      // Set selected month to most recent
      if (loadedSnapshots.length > 0) {
        const sorted = [...loadedSnapshots].sort((a, b) => b.month.localeCompare(a.month));
        setSelectedMonth(sorted[0].month);
      }
    } catch (err) {
      console.error('Error loading snapshots:', err);
      setError('Failed to load wealth snapshots');
    } finally {
      setLoading(false);
    }
  };

  const loadLiabilities = async () => {
    try {
      const liabilitiesQuery = query(
        collection(db, 'users', currentUser.uid, 'liabilities')
      );
      const liabilitiesSnapshot = await getDocs(liabilitiesQuery);
      const loadedLiabilities = [];
      liabilitiesSnapshot.forEach((doc) => {
        loadedLiabilities.push({ id: doc.id, ...doc.data() });
      });
      setLiabilities(loadedLiabilities);
    } catch (err) {
      console.error('Error loading liabilities:', err);
      // Don't set error state - liabilities are optional
    }
  };

  const loadGoals = async () => {
    try {
      const goalsQuery = query(
        collection(db, 'users', currentUser.uid, 'financialGoals')
      );
      const goalsSnapshot = await getDocs(goalsQuery);
      const loadedGoals = [];
      goalsSnapshot.forEach((doc) => {
        loadedGoals.push({ id: doc.id, ...doc.data() });
      });
      setGoals(loadedGoals);
    } catch (err) {
      console.error('Error loading goals:', err);
      // Don't set error state - goals are optional
    }
  };

  // Generate AI insights when snapshots change
  useEffect(() => {
    if (snapshots.length >= 2) {
      const analysis = analyzeWealthSnapshots(snapshots);
      setAiAnalysis(analysis);
    } else {
      setAiAnalysis(null);
    }
  }, [snapshots]);

  const handleSnapshotCreated = (newSnapshot) => {
    // Refresh snapshots
    loadSnapshots();
    // Clear editing state
    setEditingSnapshot(null);
  };

  const handleEditSnapshot = (snapshot) => {
    setEditingSnapshot(snapshot);
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditingSnapshot(null);
  };

  const handleDeleteSnapshot = async (snapshot) => {
    setDeletingSnapshot(snapshot);
  };

  const confirmDelete = async () => {
    if (!deletingSnapshot) return;

    try {
      await deleteSnapshot(currentUser.uid, deletingSnapshot.id);

      // Refresh snapshots
      await loadSnapshots();

      // If we deleted the currently selected month, select a different one
      if (selectedMonth === deletingSnapshot.month) {
        const remaining = snapshots.filter(s => s.id !== deletingSnapshot.id);
        if (remaining.length > 0) {
          // Select the most recent remaining snapshot
          const sorted = [...remaining].sort((a, b) => b.month.localeCompare(a.month));
          setSelectedMonth(sorted[0].month);
        } else {
          setSelectedMonth(null);
        }
      }

      setDeletingSnapshot(null);
    } catch (err) {
      console.error('Error deleting snapshot:', err);
      setError('Failed to delete snapshot');
      setDeletingSnapshot(null);
    }
  };

  const cancelDelete = () => {
    setDeletingSnapshot(null);
  };

  // Get snapshots for selected view period
  const filteredSnapshots = viewPeriod === 'all'
    ? snapshots
    : getRecentMonths(snapshots, parseInt(viewPeriod));

  // Get current and previous snapshot
  const currentSnapshot = snapshots.find(s => s.month === selectedMonth);
  const sortedSnapshots = [...snapshots].sort((a, b) => a.month.localeCompare(b.month));
  const currentIndex = sortedSnapshots.findIndex(s => s.month === selectedMonth);
  const previousSnapshot = currentIndex > 0 ? sortedSnapshots[currentIndex - 1] : null;

  // ⭐ LIVE PRICING: Fetch live prices for investments
  const { prices: priceMap, loading: pricesLoading } = usePortfolioPrices(investments);

  // ⭐ Calculate LIVE current net worth (updates dynamically with investments/liabilities)
  const displayCurrency = getCurrencyCode(selectedCountry);
  const liveNetWorthData = calculateNetWorth({
    investments,
    cryptoHoldings,
    liabilities,
    cashReserves,
    displayCurrency,
    exchangeRates,
    priceMap  // ⭐ LIVE PRICING: Pass live prices
  });

  // Empty state
  if (loading || pricesLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader className={`w-8 h-8 animate-spin mx-auto mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Loading wealth tracking data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-6 rounded-lg border ${isDark ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-200'}`}>
        <div className="flex items-center gap-2 text-red-500">
          <AlertCircle className="w-5 h-5" />
          <p className="font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  if (snapshots.length === 0) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <div
          className={`mx-auto max-w-md p-6 sm:p-8 rounded-lg border ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          <TrendingUp className={`w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
          <h3 className={`text-lg sm:text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Start Tracking Your Wealth
          </h3>
          <p className={`text-sm sm:text-base mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Create your first wealth snapshot to see how your net worth changes over time,
            with detailed breakdowns of contributions, market growth, and debt paydown.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-5 py-2.5 sm:px-6 sm:py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold flex items-center gap-2 mx-auto text-sm sm:text-base"
          >
            <Plus className="w-5 h-5" />
            Create Your First Snapshot
          </button>

          <div className={`mt-6 p-3 sm:p-4 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
            <p className={`text-xs sm:text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
              💡 Tip: Create snapshots monthly to track your financial progress. January 2026 is a great time to start!
            </p>
          </div>
        </div>

        <CreateSnapshotModal
          isOpen={showCreateModal}
          onClose={handleCloseModal}
          onSnapshotCreated={handleSnapshotCreated}
          editingSnapshot={editingSnapshot}
          accounts={accounts}
          investments={investments}
          cryptoHoldings={cryptoHoldings}
          liabilities={liabilities}
          cashReserves={cashReserves}
          exchangeRates={exchangeRates}
          isDark={isDark}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Wealth Tracking
          </h2>
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Track your net worth changes over time with detailed attribution
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2">
          {/* View period selector */}
          <select
            value={viewPeriod}
            onChange={(e) => setViewPeriod(e.target.value)}
            className={`px-3 py-2 rounded-lg border text-sm sm:text-base ${
              isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="12">Last 12 Months</option>
            <option value="24">Last 24 Months</option>
            <option value="all">All Time</option>
          </select>

          {/* Edit and Delete buttons - show only when viewing a snapshot */}
          {currentSnapshot && (
            <>
              <button
                onClick={() => handleEditSnapshot(currentSnapshot)}
                className={`px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap ${
                  isDark
                    ? 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-600'
                    : 'bg-white hover:bg-gray-50 text-gray-900 border border-gray-300'
                }`}
                title="Edit contributions and withdrawals for this snapshot"
              >
                <Edit2 className="w-4 h-4" />
                <span className="hidden sm:inline">Edit</span>
              </button>

              <button
                onClick={() => handleDeleteSnapshot(currentSnapshot)}
                className={`px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap ${
                  isDark
                    ? 'bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-800'
                    : 'bg-red-50 hover:bg-red-100 text-red-600 border border-red-200'
                }`}
                title="Delete this snapshot"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Delete</span>
              </button>
            </>
          )}

          <button
            onClick={() => { setEditingSnapshot(null); setShowCreateModal(true); }}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">Create Snapshot</span>
            <span className="sm:hidden">New Snapshot</span>
          </button>
        </div>
      </div>

      {/* ⭐ Current Live Net Worth Card */}
      <div className={`rounded-xl p-6 border ${
        isDark ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700' : 'bg-gradient-to-br from-white to-gray-50 border-gray-200'
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Current Net Worth
              </h3>
              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20">
                <Radio className="w-3 h-3 text-green-500 animate-pulse" />
                <span className="text-xs font-semibold text-green-500">LIVE</span>
              </div>
              <InfoTooltip
                content="Your live net worth based on current investments and liabilities. This updates instantly when you add or remove assets."
                isDark={isDark}
              />
            </div>
            <p className={`text-4xl sm:text-5xl font-bold ${liveNetWorthData.netWorth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {formatCurrency(liveNetWorthData.netWorth, selectedCountry)}
            </p>
            <div className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              <span>Assets: {formatCurrency(liveNetWorthData.totalAssets, selectedCountry)}</span>
              <span className="mx-2">•</span>
              <span>Liabilities: {formatCurrency(liveNetWorthData.totalLiabilities, selectedCountry)}</span>
            </div>
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            <p className="text-center sm:text-right font-semibold">
              As of {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </p>
            <p className="text-xs mt-1 text-center sm:text-right">
              {snapshots.length > 0 ? 'Create a snapshot to track changes' : 'Create your first snapshot'}
            </p>
          </div>
        </div>
      </div>

      {/* Onboarding Banner */}
      {snapshots.length <= 2 && (
        <OnboardingBanner snapshotCount={snapshots.length} isDark={isDark} />
      )}

      {/* Timeline */}
      {snapshots.length > 1 && (
        <SnapshotTimeline
          snapshots={snapshots}
          selectedMonth={selectedMonth}
          onMonthSelect={setSelectedMonth}
          isDark={isDark}
        />
      )}

      {/* Month-over-Month Cards */}
      <MonthOverMonthCard
        currentSnapshot={currentSnapshot}
        previousSnapshot={previousSnapshot}
        isDark={isDark}
      />

      {/* AI Insights - Show if we have enough data */}
      {aiAnalysis && (
        <AIInsights analysis={aiAnalysis} isDark={isDark} />
      )}

      {/* Goal Overlay - Show if user has goals */}
      {currentSnapshot && goals.length > 0 && (
        <GoalOverlay
          goals={goals}
          currentNetWorth={currentSnapshot.netWorth}
          snapshots={snapshots}
          isDark={isDark}
        />
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Net Worth Trend */}
        <div
          className={`rounded-lg border p-6 ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          <div className="flex items-center gap-2 mb-4">
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Net Worth Trend
            </h3>
            <InfoTooltip
              content="Your net worth over time (Assets - Liabilities). This shows your overall financial trajectory and helps you visualize long-term wealth building."
              isDark={isDark}
              position="bottom"
            />
          </div>
          <NetWorthTrendChart snapshots={filteredSnapshots} isDark={isDark} />
        </div>

        {/* Growth Attribution */}
        <div
          className={`rounded-lg border p-6 ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          <div className="flex items-center gap-2 mb-4">
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Growth Attribution
            </h3>
            <InfoTooltip
              content="Month-by-month breakdown showing what's driving your net worth changes: your contributions, market gains/losses, debt paydown, withdrawals, and interest paid. This helps you understand whether your wealth is growing from saving or investing."
              isDark={isDark}
              position="bottom"
            />
          </div>
          <GrowthAttributionChart snapshots={filteredSnapshots} isDark={isDark} />
        </div>
      </div>

      {/* Account Performance Table */}
      <div>
        <AccountPerformanceTable
          currentSnapshot={currentSnapshot}
          previousSnapshot={previousSnapshot}
          isDark={isDark}
        />
      </div>

      {/* Info Card */}
      {snapshots.length < 2 && (
        <div
          className={`p-4 rounded-lg ${
            isDark ? 'bg-blue-900/20 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'
          }`}
        >
          <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
            💡 Create at least 2 snapshots to see growth trends and month-over-month comparisons.
          </p>
        </div>
      )}

      {/* Create/Edit Snapshot Modal */}
      <CreateSnapshotModal
        isOpen={showCreateModal}
        onClose={handleCloseModal}
        onSnapshotCreated={handleSnapshotCreated}
        editingSnapshot={editingSnapshot}
        accounts={accounts}
        investments={investments}
        cryptoHoldings={cryptoHoldings}
        liabilities={liabilities}
        cashReserves={cashReserves}
        exchangeRates={exchangeRates}
        isDark={isDark}
      />

      {/* Delete Confirmation Modal */}
      {deletingSnapshot && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-lg border max-w-md w-full p-6 ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}
          >
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-full ${isDark ? 'bg-red-900/30' : 'bg-red-50'}`}>
                <AlertCircle className={`w-6 h-6 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
              </div>
              <div className="flex-1">
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Delete Snapshot
                </h3>
                <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Are you sure you want to delete the snapshot for{' '}
                  <strong className={isDark ? 'text-white' : 'text-gray-900'}>
                    {formatMonthLabel(deletingSnapshot.month)}
                  </strong>
                  ? This action cannot be undone.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={cancelDelete}
                    className={`flex-1 px-4 py-2 rounded-lg font-semibold ${
                      isDark
                        ? 'bg-gray-700 hover:bg-gray-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WealthTrackingDashboard;
